import {
  IStaffer,
  type IPracticeSetupWizard,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  toMoment,
  type Timestamp,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';

export enum SetupWizardEngagementStatus {
  Active = 'active',
  Slowing = 'slowing',
  Disengaged = 'disengaged',
  NeverEngaged = 'neverEngaged',
  NoData = 'noData',
}

/**
 * Maps each engagement status to a numeric severity level.
 * Higher values indicate worse engagement, used to determine the
 * worst-case status across multiple staff members.
 */
const ENGAGEMENT_SEVERITY: Record<SetupWizardEngagementStatus, number> = {
  [SetupWizardEngagementStatus.Disengaged]: 4,
  [SetupWizardEngagementStatus.NeverEngaged]: 3,
  [SetupWizardEngagementStatus.Slowing]: 2,
  [SetupWizardEngagementStatus.Active]: 1,
  [SetupWizardEngagementStatus.NoData]: 0,
};

export class SetupWizardEngagement {
  /**
   * Classifies a single staffer's engagement state.
   * - NoData: both lastEngagement and assignedAt undefined (old wizard, no data)
   * - NeverEngaged: no engagement AND assigned ≥ 3 days ago
   * - Active: engaged within 3 days, OR assigned < 3 days ago with no engagement yet
   * - Slowing: last engagement 3–7 days ago
   * - Disengaged: last engagement > 7 days ago
   */
  static classifyStafferEngagement(
    lastEngagement: Timestamp | undefined,
    assignedAt: Timestamp | undefined,
    now: moment.Moment
  ): SetupWizardEngagementStatus {
    if (!lastEngagement && !assignedAt) {
      return SetupWizardEngagementStatus.NoData;
    }

    if (!lastEngagement) {
      if (!assignedAt) {
        return SetupWizardEngagementStatus.NoData;
      }

      const daysSinceAssignment = now.diff(toMoment(assignedAt), 'days');

      return daysSinceAssignment >= 3
        ? SetupWizardEngagementStatus.NeverEngaged
        : SetupWizardEngagementStatus.Active;
    }

    const daysSinceEngagement = now.diff(toMoment(lastEngagement), 'days');

    if (daysSinceEngagement <= 3) {
      return SetupWizardEngagementStatus.Active;
    }

    if (daysSinceEngagement <= 7) {
      return SetupWizardEngagementStatus.Slowing;
    }

    return SetupWizardEngagementStatus.Disengaged;
  }

  /**
   * Classifies the wizard-wide engagement state = WORST across all assignedStaff.
   * Severity order: Disengaged > NeverEngaged > Slowing > Active > NoData.
   * Returns NoData only if ALL staff are NoData.
   */
  static classifyWizardEngagement(
    wizard: IPracticeSetupWizard,
    staffRefs: DocumentReference<IStaffer>[],
    now: moment.Moment
  ): SetupWizardEngagementStatus {
    let worstState = SetupWizardEngagementStatus.NoData;

    for (const stafferRef of staffRefs) {
      const stafferId = stafferRef.id;
      const stafferState = SetupWizardEngagement.classifyStafferEngagement(
        wizard.lastEngagementByStaffer?.[stafferId],
        wizard.assignedStaffAt?.[stafferId],
        now
      );

      if (ENGAGEMENT_SEVERITY[stafferState] > ENGAGEMENT_SEVERITY[worstState]) {
        worstState = stafferState;
      }
    }

    return worstState;
  }
}
